
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { AlertTriangle } from 'lucide-react';

interface GalleryItemProps {
  id: string;
  title: string;
  author: string;
  imageUrl: string;
  genre: string;
  rating: number;
  summary: string;
  critique?: string;
}

const GalleryItem: React.FC<GalleryItemProps> = ({
  id,
  title,
  author,
  imageUrl,
  genre,
  rating,
  summary,
  critique
}) => {
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);

  const handleViewDetails = () => {
    // Navigate to detail page with ID
    navigate(`/gallery/${id}`);
  };

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <Card className="overflow-hidden transition-all hover:shadow-md border-bookNavy/20">
      <div className="overflow-hidden relative">
        <AspectRatio ratio={2/3}>
          {imageError ? (
            <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-500 p-2 text-center">
              <div>
                <AlertTriangle className="h-8 w-8 mx-auto mb-1 text-amber-500" />
                <p className="text-xs">Image not available</p>
              </div>
            </div>
          ) : (
            <img 
              src={imageUrl} 
              alt={`${title} by ${author}`} 
              className="object-cover w-full h-full"
              onError={handleImageError}
            />
          )}
          <div className={`absolute top-2 right-2 w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
            rating >= 7 ? 'bg-green-500' : rating <= 3 ? 'bg-red-500' : 'bg-amber-500'
          }`}>
            {rating}
          </div>
        </AspectRatio>
      </div>
      <CardHeader className="py-3 px-4">
        <CardTitle className="text-base font-medium line-clamp-1">{title}</CardTitle>
        <p className="text-sm text-gray-500">by {author}</p>
      </CardHeader>
      <CardContent className="py-0 px-4">
        <p className="text-xs text-gray-600 mb-2">{genre}</p>
        <p className="text-sm line-clamp-2">{summary}</p>
      </CardContent>
      <CardFooter className="pt-2 pb-4 px-4">
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full"
          onClick={handleViewDetails}
        >
          View Details
        </Button>
      </CardFooter>
    </Card>
  );
};

export default GalleryItem;
